import React, { useState, useEffect } from 'react';
import './RenameChatDialog.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useI18n } from '../../../contexts/I18nContext';
import { RootState, AppDispatch } from '../../../../store';
import { updateChatOnServer } from '../../../../chatSlice';

interface RenameChatDialogProps {
    isOpen: boolean;
    onClose: () => void;
    chatId: string;
    currentTitle: string;
}

const RenameChatDialog: React.FC<RenameChatDialogProps> = ({
    isOpen,
    onClose,
    chatId,
    currentTitle
}): React.ReactElement | null => {
    const dispatch = useDispatch<AppDispatch>();
    const { t } = useI18n();
    const [newTitle, setNewTitle] = useState(currentTitle);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Обновляем состояние при изменении currentTitle
    useEffect(() => {
        setNewTitle(currentTitle);
        setError(null);
    }, [currentTitle, isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!newTitle.trim()) {
            setError(t('dialogs.renameEmpty'));
            return;
        }

        if (newTitle.trim() === currentTitle) {
            onClose();
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // Обновляем чат на сервере (если онлайн) или локально
            const result = await dispatch(updateChatOnServer({
                chatId,
                updateData: { title: newTitle.trim() }
            }) as any);

            if (updateChatOnServer.fulfilled.match(result)) {
                onClose();
            } else {
                throw new Error('Failed to update chat');
            }
        } catch (error) {
            console.error('Ошибка переименования чата:', error);
            setError(t('dialogs.renameError'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="rename-chat-dialog-overlay" onClick={onClose}>
            <div className="rename-chat-dialog" onClick={(e) => e.stopPropagation()}>
                <div className="rename-chat-dialog-header">
                    <h3>{t('dialogs.renameChat')}</h3>
                    <button
                        className="rename-chat-dialog-close"
                        onClick={onClose}
                        disabled={isLoading}
                    >
                        ×
                    </button>
                </div>

                <form className="rename-chat-dialog-content" onSubmit={handleSubmit}>
                    <div className="rename-chat-field">
                        <label htmlFor="chat-title">{t('dialogs.renameChatTitle')}</label>
                        <input
                            id="chat-title"
                            type="text"
                            className="input-secondary"
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={t('dialogs.renamePlaceholder')}
                            maxLength={200}
                            disabled={isLoading}
                            autoFocus
                        />
                        <div className="char-count">
                            {newTitle.length}/200
                        </div>
                    </div>

                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}

                    <div className="rename-chat-actions">
                        <button
                            type="button"
                            className="d-none d-md-block button-secondary ps-3 pe-3"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            {t('dialogs.cancel')}
                        </button>
                        <button
                            type="submit"
                            className="button-primary"
                            disabled={isLoading || !newTitle.trim()}
                        >
                            {isLoading ? t('dialogs.saving') : t('dialogs.save')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RenameChatDialog;
